import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useToast } from '@/hooks/use-toast';
import { useAuthHeaders } from '@/lib/auth-store';
import { fmtDateTime } from '@/lib/dateFormat';
import { Plus, Play, Trash2, Workflow, ArrowRight, Clock, ToggleLeft, ToggleRight } from 'lucide-react';
import { 
  useListFlows, 
  useDeleteFlow, 
  useRunFlow,
  useUpdateFlow,
  getListFlowsQueryKey
} from '@workspace/api-client-react';

const ACTION_LABELS: Record<string, string> = {
  sync_inventory: "Sync Inventory",
  sync_products: "Sync Products",
  sync_prices: "Sync Prices",
  sync_orders: "Sync Orders",
};

export function Flows() {
  const [_, setLocation] = useLocation();
  const authOpts = useAuthHeaders();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: flows, isLoading } = useListFlows({ request: authOpts });
  const deleteMutation = useDeleteFlow({ request: authOpts });
  const runMutation = useRunFlow({ request: authOpts });
  const updateMutation = useUpdateFlow({ request: authOpts });

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this automation flow?")) return;
    try {
      await deleteMutation.mutateAsync({ id });
      queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey() });
      toast({ title: "Flow deleted" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleRun = async (id: string) => {
    try {
      await runMutation.mutateAsync({ id });
      toast({ title: "Flow triggered", description: "The automation is now running." });
      queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey() });
    } catch (err: any) {
      toast({ title: "Run Failed", description: err.message, variant: "destructive" });
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    try {
      await updateMutation.mutateAsync({ id, data: { isActive: !currentState } });
      queryClient.invalidateQueries({ queryKey: getListFlowsQueryKey() });
      toast({ title: `Flow ${!currentState ? 'activated' : 'paused'}` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Automation Flows</h1>
          <p className="text-muted-foreground">Configure data sync rules between your connected stores.</p>
        </div>
        <Button onClick={() => setLocation('/flows/new')} className="gap-2">
          <Plus className="w-4 h-4" /> Create Flow
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4 animate-pulse">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-muted/50 rounded-2xl"></div>)}
        </div>
      ) : flows?.length === 0 ? (
        <div className="glass-panel rounded-3xl p-16 text-center border-dashed border-border">
          <Workflow className="w-16 h-16 mx-auto mb-6 text-slate-600" />
          <h3 className="text-xl font-display mb-2">No active flows</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">Create a workflow to automate inventory, product, or order synchronization between stores.</p>
          <Button onClick={() => setLocation('/flows/new')}>Create your first flow</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {flows?.map((flow) => (
            <div key={flow.id} className={`glass-panel rounded-2xl p-5 border transition-all ${flow.isActive ? 'border-border hover:border-primary/30' : 'border-border/50 opacity-70 grayscale-[0.3]'}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-bold text-foreground">{flow.name}</h3>
                    <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/20">
                      {ACTION_LABELS[flow.actionType] || flow.actionType}
                    </span>
                    {!flow.isActive && <span className="text-xs font-bold text-muted-foreground bg-card px-2 py-0.5 rounded uppercase">Paused</span>}
                  </div>
                  
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-3 bg-muted/50 p-2 rounded-lg inline-flex border border-border/50">
                    <span className="font-medium text-foreground">{flow.sourceStore?.nickname || 'Unknown'}</span>
                    <ArrowRight className="w-4 h-4 text-primary" />
                    <span className="font-medium text-foreground">{flow.destStore?.nickname || 'Unknown'}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  {flow.schedule ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-lg border border-border/50">
                      <Clock className="w-4 h-4 text-emerald-400" />
                      <span className="font-mono">{flow.schedule}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">Manual execution only</span>
                  )}

                  <div className="text-right min-w-[120px]">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Last Run</p>
                    {flow.lastRunAt ? (
                      <div>
                        <p className={`text-sm font-medium ${flow.lastRunStatus === 'success' ? 'text-emerald-400' : flow.lastRunStatus === 'error' ? 'text-rose-400' : 'text-amber-400'}`}>
                          {flow.lastRunStatus}
                        </p>
                        <p className="text-xs text-muted-foreground">{fmtDateTime(flow.lastRunAt)}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Never</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pl-4 border-l border-border">
                    <button 
                      onClick={() => toggleActive(flow.id, flow.isActive)}
                      className="p-2 hover:bg-muted rounded-xl transition-colors text-muted-foreground hover:text-foreground"
                      title={flow.isActive ? "Pause Flow" : "Activate Flow"}
                    >
                      {flow.isActive ? <ToggleRight className="w-6 h-6 text-primary" /> : <ToggleLeft className="w-6 h-6" />}
                    </button>
                    
                    <Button 
                      variant="secondary" 
                      size="sm"
                      className="gap-2" 
                      onClick={() => handleRun(flow.id)}
                      isLoading={runMutation.isPending && runMutation.variables?.id === flow.id}
                      disabled={!flow.isActive}
                    >
                      <Play className="w-3.5 h-3.5" /> Run
                    </Button>

                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDelete(flow.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
